import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Nothing } from 'components/Nothing';
import moment from 'moment';
import { FC, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ReactComponent as IconPencil } from './img/iconPencil.svg';
import styles from './Categories.module.scss';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import { useMediaQuery } from '@mantine/hooks';
import { useGetCategoryEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { CreateEmployedsСategory } from '../../CreateEmployedsСategory';
import { Button } from 'ui/Button';
import { Loader } from '@mantine/core';

interface Props {
  openNav: boolean | undefined;
  setShowCopiedSuccess: (value: boolean) => void;
  textSuccess: (e: string) => void;
}

export const Categories: FC<Props> = ({ openNav, setShowCopiedSuccess, textSuccess }) => {
  const [categoryParams, setCategoryParams] = useState({ page: 1 });
  const {
    data: dataCategory,
    isLoading,
    isFetching,
  } = useGetCategoryEmployedsQuery(categoryParams);

  const hasNextPage = dataCategory?.meta
    ? dataCategory.meta.last_page !== dataCategory.meta.current_page
    : false;

  const showFullPageSkeleton = isLoading || (isFetching && categoryParams.page === 1);

  const [intersectionRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setCategoryParams((prev) => ({ ...prev, page: prev.page + 1 })),
    rootMargin: '0px 0px 160px 0px',
  });

  const isLessThan1024px = useMediaQuery('(width < 1024px)');
  const isLessThan1300px = useMediaQuery('(width < 1300px)');
  const isDrawerCategoryForm = useMediaQuery('(max-width: 768px)') ?? false;

  const [open, setOpen] = useState(false);
  const [initialData, setInitialData] = useState<{ id: string; name: string } | null>(
    null,
  );

  const showFullTable = !((!openNav && isLessThan1300px) || isLessThan1024px);

  if (!!showFullPageSkeleton) {
    return (
      <div className={styles.progressBlock}>
        <LoadingSkeleton fullWidth={true} />
      </div>
    );
  }

  const handleOpen = (item: any) => {
    setOpen(true);

    const initialData = {
      id: item.id,
      name: item.name,
    };

    setInitialData(initialData);
  };

  return (
    <div>
      {showFullTable ? (
        <div className={styles.table}>
          <div className={styles.table_title}>
            <p>Название</p>
            <p>Исполнителей в группе</p>
            <p>Создал</p>
            <p>Дата создания</p>
            <p>Дата редактирования</p>
          </div>
          {dataCategory?.data?.length ? (
            dataCategory?.data.map((item) => (
              <div
                className={styles.table_row}
                key={item.id}
                onClick={() => handleOpen(item)}>
                <TextWithTooltip content={item.name} />
                <TextWithTooltip content={item.workers_count} />
                <TextWithTooltip
                  content={`${item.created_by.name} ${item.created_by.patronymic}`}
                />
                <TextWithTooltip
                  content={moment(item.created_at).format('DD MMMM').replace('.', '')}
                />
                <TextWithTooltip
                  content={moment(item.updated_at).format('DD MMMM').replace('.', '')}
                />
              </div>
            ))
          ) : (
            <Nothing style={{ height: '200px' }} title='Ничего не найдено' />
          )}
          {hasNextPage ? <div style={{ height: '10px' }} ref={intersectionRef} /> : null}
        </div>
      ) : (
        <div>
          {dataCategory?.data?.length ? (
            dataCategory?.data.map((item) => (
              <div key={item.id} className={styles.mobile_row}>
                <div className={styles.mobile_content}>
                  <div className={styles.mobile_header}>
                    <div className={styles.name}>
                      <TextWithTooltip
                        content={item.name}
                        className={styles.nameText}
                        isMobile={true}
                      />
                    </div>

                    <Button
                      theme='outline'
                      className={styles.icon_pencil}
                      onClick={() => handleOpen(item)}>
                      <IconPencil />
                    </Button>
                  </div>

                  <div className={styles.mobile_details}>
                    <div className={styles.line_content_mobile}>
                      <p>Исполнителей</p>
                      <TextWithTooltip
                        content={item.workers_count}
                        className={styles.context}
                        isMobile={true}
                      />
                    </div>

                    <div className={styles.line_content_mobile}>
                      <p>Создал</p>
                      <TextWithTooltip
                        content={`${item.created_by.name} ${item.created_by.patronymic}`}
                        className={styles.context}
                        isMobile={true}
                      />
                    </div>

                    <div className={styles.line_content_mobile}>
                      <p>Дата создания</p>
                      <TextWithTooltip
                        className={styles.context}
                        content={moment(item.created_at)
                          .format('DD MMMM')
                          .replace('.', '')}
                        isMobile={true}
                      />
                    </div>

                    <div className={styles.line_content_mobile}>
                      <p>Дата редактирования</p>
                      <TextWithTooltip
                        className={styles.context}
                        content={moment(item.updated_at)
                          .format('DD MMMM')
                          .replace('.', '')}
                        isMobile={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <Nothing style={{ height: '200px' }} title='Ничего не найдено' />
          )}
          {hasNextPage ? <div style={{ height: '10px' }} ref={intersectionRef} /> : null}
        </div>
      )}

      {!isLoading &&
        isFetching &&
        categoryParams.page > 1 &&
        (dataCategory?.data?.length ?? 0) > 0 && (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Loader size='xs' color='#002cfb' />
          </div>
        )}

      {open && (
        <CreateEmployedsСategory
          variant={isDrawerCategoryForm ? 'drawer' : 'modal'}
          setIsShowFinishModal={setOpen}
          initialData={initialData}
          setShowCopiedSuccess={setShowCopiedSuccess}
          setInitialData={setInitialData}
          textSuccess={textSuccess}
        />
      )}
    </div>
  );
};
